import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { stripLangPrefix } from "./langRoute";

/**
 * "Torna al borgo": quando l'utente fa login da /comune/{slug} o /citta/{slug}
 * deve rientrare sulla stessa scheda, in modalità Editor (box di pubblicazione
 * direttamente nella Piazza digitale del borgo).
 */

const TOWN_RE = /^\/(comune|citta|città)\/([^/?#]+)/i;
const LAST_TOWN_KEY = "6amico_last_town_visit";
const LAST_TOWN_TTL_MS = 2 * 60 * 60 * 1000; // 2 ore

/** Se il path (con o senza prefisso lingua) è una scheda borgo/città → {kind, slug}. */
export function parseTownPath(pathname) {
  const bare = stripLangPrefix(String(pathname || "")).split("?")[0].split("#")[0];
  const m = bare.match(TOWN_RE);
  if (!m) return null;
  return { kind: m[1].toLowerCase() === "comune" ? "comune" : "citta", slug: decodeURIComponent(m[2]) };
}

export function isTownPath(pathname) {
  return Boolean(parseTownPath(pathname));
}

/** Aggiunge/aggiorna query params su un path (preserva hash). */
export function withQuery(path, params) {
  const [beforeHash, hash = ""] = String(path).split("#");
  const [base, qs = ""] = beforeHash.split("?");
  const sp = new URLSearchParams(qs);
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") sp.delete(k);
    else sp.set(k, String(v));
  });
  const q = sp.toString();
  return `${base}${q ? `?${q}` : ""}${hash ? `#${hash}` : ""}`;
}

/**
 * Path di ritorno dopo il login: per le schede borgo/città forza `editor=1`
 * (modalità Editor), per le altre pagine resta invariato.
 */
export function loginReturnForPage(pathname, search = "", extra = {}) {
  const full = `${pathname || "/"}${search || ""}`;
  if (!isTownPath(pathname)) return full;
  return withQuery(full, { editor: "1", ...extra });
}

/** URL di login che riporta alla pagina corrente. */
export function loginUrlForPage(pathname, search = "", extra = {}) {
  return `/login?return=${encodeURIComponent(loginReturnForPage(pathname, search, extra))}`;
}

/** Ricorda l'ultima scheda borgo/città visitata in questa sessione. */
export function rememberTownVisit(pathname, search = "") {
  try {
    if (!isTownPath(pathname)) return;
    const clean = withQuery(`${pathname}${search || ""}`, { editor: null, intent: null });
    sessionStorage.setItem(LAST_TOWN_KEY, JSON.stringify({ path: clean, at: Date.now() }));
  } catch {
    /* sessionStorage non disponibile */
  }
}

/** Ultima scheda borgo visitata (se recente) già in modalità Editor, oppure "". */
export function recallTownReturn() {
  try {
    const raw = sessionStorage.getItem(LAST_TOWN_KEY);
    if (!raw) return "";
    const { path, at } = JSON.parse(raw);
    if (!path || Date.now() - Number(at) > LAST_TOWN_TTL_MS) return "";
    return loginReturnForPage(path.split("?")[0], path.includes("?") ? `?${path.split("?")[1]}` : "");
  } catch {
    return "";
  }
}

/** Slug dell'ultima scheda borgo/città visitata di recente (per precompilare la dashboard). */
export function recallTownSlug() {
  try {
    const raw = sessionStorage.getItem(LAST_TOWN_KEY);
    if (!raw) return "";
    const { path, at } = JSON.parse(raw);
    if (!path || Date.now() - Number(at) > LAST_TOWN_TTL_MS) return "";
    return parseTownPath(path.split("?")[0])?.slug || "";
  } catch {
    return "";
  }
}

/** Hook: da chiamare nelle schede /comune e /citta. */
export function useRememberTown() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    rememberTownVisit(pathname, search);
  }, [pathname, search]);
}
